'use client';

import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { VERIFICATION_COPY, FARM_EQUIPMENT_LABEL } from '@/lib/features';
import { Beef, Tractor, Wrench, Star } from 'lucide-react';
import AdBanner from '@/components/AdBanner';
import VerifiedBadge from '@/components/VerifiedBadge';
import { createClient } from '@/lib/supabase/client';
import {
  fetchListings,
  formatPrice,
  listedAgo,
  primaryImage,
  type Listing,
} from '@/lib/listings';

type Category = 'livestock' | 'machinery' | 'farm-equipment';

// The three browse tabs map onto listings.category. Livestock and machinery are
// one-to-one; the third tab is everything else that trades on the site (deadstock,
// haulage, crops and feed) so nothing a seller posts becomes unreachable.
const CATEGORY_FILTER: Record<Category, { category?: string; exclude?: string[] }> = {
  livestock: { category: 'livestock' },
  machinery: { category: 'machinery' },
  // Grain, feed and bedding has its own page now, so it comes out of the
  // catch-all — otherwise the same load of barley shows up in two places.
  'farm-equipment': { exclude: ['livestock', 'machinery', 'crops-feed'] },
};

const speciesOptions = ['Cattle', 'Sheep', 'Pigs', 'Horses', 'Poultry', 'Goats', 'Deer', 'Alpacas'];
const machineryCategories = ['Tractors', 'Combine Harvesters', 'Balers', 'Sprayers', 'Trailers', 'Ploughs', 'Cultivators', 'Other'];

const ukCounties = [
  'Bedfordshire','Berkshire','Bristol','Buckinghamshire','Cambridgeshire','Cheshire','Cornwall',
  'Cumbria','Derbyshire','Devon','Dorset','Durham','East Riding of Yorkshire','East Sussex','Essex',
  'Gloucestershire','Greater London','Greater Manchester','Hampshire','Herefordshire','Hertfordshire',
  'Isle of Wight','Kent','Lancashire','Leicestershire','Lincolnshire','Merseyside','Norfolk',
  'North Yorkshire','Northamptonshire','Northumberland','Nottinghamshire','Oxfordshire','Rutland',
  'Shropshire','Somerset','South Yorkshire','Staffordshire','Suffolk','Surrey','Tyne and Wear',
  'Warwickshire','West Midlands','West Sussex','West Yorkshire','Wiltshire','Worcestershire',
  'Aberdeenshire','Angus','Argyll and Bute','Dumfries and Galloway','Edinburgh','Fife','Glasgow',
  'Highland','Perth and Kinross','Scottish Borders','Stirling',
  'Anglesey','Cardiff','Carmarthenshire','Ceredigion','Conwy','Denbighshire','Gwynedd',
  'Monmouthshire','Pembrokeshire','Powys','Swansea','Wrexham',
  'Antrim','Armagh','Down','Fermanagh','Londonderry','Tyrone',
];

const PAGE_SIZE = 24;

/**
 * This screen appears on the homepage as well as /marketplace-listings, and it
 * reads the ?cat= / ?q= params. useSearchParams() opts a page out of static
 * prerendering unless it sits inside a Suspense boundary, so the boundary lives
 * here rather than being every caller's problem.
 */
export default function MarketplaceListingsScreen() {
  return (
    <Suspense fallback={<MarketplaceLoadingFallback />}>
      <MarketplaceListings />
    </Suspense>
  );
}

function MarketplaceLoadingFallback() {
  return (
    <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      <div className="h-8 w-72 bg-muted rounded animate-pulse mb-8" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={`fallback-${i}`} className="card p-0 overflow-hidden border border-border animate-pulse">
            <div className="h-44 bg-muted" />
            <div className="p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

function MarketplaceListings() {
  const searchParams = useSearchParams();

  // The header links here with ?cat= and the homepage search box with ?q=, so
  // seed the filters from the URL rather than always opening on livestock.
  const catParam = searchParams.get('cat');
  const initialCategory: Category =
    catParam === 'machinery' || catParam === 'farm-equipment' || catParam === 'livestock'
      ? catParam
      : 'livestock';

  const [activeCategory, setActiveCategory] = useState<Category>(initialCategory);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') ?? '');
  const [selectedCounty, setSelectedCounty] = useState('All Counties');
  const [selectedSpecies, setSelectedSpecies] = useState<string[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [priceMax, setPriceMax] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const [listings, setListings] = useState<Listing[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const supabase = useMemo(() => createClient(), []);

  // Debounced so typing in the search box doesn't fire a query per keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { category, exclude } = CATEGORY_FILTER[activeCategory];
      const { listings: rows, total: count } = await fetchListings(supabase, {
        category,
        excludeCategories: exclude,
        county: selectedCounty === 'All Counties' ? undefined : selectedCounty,
        q: debouncedQuery || undefined,
        maxPrice: priceMax ? Number(priceMax) : undefined,
        verifiedOnly,
        species: selectedSpecies.length ? selectedSpecies : undefined,
        sort: sortBy as 'newest' | 'price-asc' | 'price-desc' | 'popular',
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setListings(rows);
      setTotal(count);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not load listings. Please try again shortly.'
      );
      setListings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    supabase,
    activeCategory,
    selectedCounty,
    debouncedQuery,
    priceMax,
    verifiedOnly,
    selectedSpecies,
    sortBy,
    page,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change puts us back on page one.
  useEffect(() => {
    setPage(0);
  }, [activeCategory, selectedCounty, debouncedQuery, priceMax, verifiedOnly, selectedSpecies, sortBy]);

  const toggleSpecies = (value: string) =>
    setSelectedSpecies((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    );

  const categoryLabel = activeCategory === 'farm-equipment' ? 'farm equipment' : activeCategory;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Agricultural Marketplace</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {loading ? 'Loading listings…' : `${total} listing${total === 1 ? '' : 's'} in ${categoryLabel}`}
          </p>
        </div>
        <Link href="/post-listing" className="btn-accent self-start md:self-auto">
          <Icon name="PlusCircleIcon" size={18} />
          Post a Listing
        </Link>
      </div>

      {/* Leaderboard ad */}
      <div className="flex justify-center mb-6">
        <AdBanner width={970} height={90} label="970×90 Mid-Page Banner — Prime Placement" className="rounded-lg" />
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6 w-fit max-w-full overflow-x-auto">
        {(['livestock', 'machinery', 'farm-equipment'] as Category[]).map((cat) =>
        <button
          key={`cat-tab-${cat}`}
          onClick={() => setActiveCategory(cat)}
          className={`px-5 py-2 rounded-lg text-sm font-semibold capitalize transition-all duration-200 shrink-0 whitespace-nowrap ${
          activeCategory === cat ?
          'bg-card text-primary shadow-sm' :
          'text-muted-foreground hover:text-foreground'}`
          }>
            <span className="inline-flex items-center gap-1.5">
              {cat === 'livestock' && <Beef className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />}
              {cat === 'machinery' && <Tractor className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />}
              {cat === 'farm-equipment' && <Wrench className="w-4 h-4" strokeWidth={1.75} aria-hidden="true" />}
              {cat === 'farm-equipment' ? FARM_EQUIPMENT_LABEL : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </span>
          </button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar filters */}
        <aside className="hidden lg:flex flex-col gap-4 w-64 xl:w-72 shrink-0">
          {/* Search */}
          <div className="card">
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">Search</h3>
            <div className="relative">
              <input
                type="text"
                className="form-input pl-9"
                placeholder={activeCategory === 'livestock' ? 'Breed, type, seller…' : 'Make, model, type…'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} />

              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>

          {/* Species / Categories */}
          <div className="card">
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">
              {activeCategory === 'livestock' ? 'Species' : 'Category'}
            </h3>
            <div className="flex flex-col gap-1.5">
              {(activeCategory === 'livestock' ? speciesOptions : machineryCategories).map((opt) =>
              <label key={`filter-spec-${opt}`} className="flex items-center gap-2 cursor-pointer py-0.5">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-primary rounded"
                    checked={selectedSpecies.includes(opt.toLowerCase())}
                    onChange={() => toggleSpecies(opt.toLowerCase())} />
                  <span className="text-sm text-foreground">{opt}</span>
                </label>
              )}
            </div>
            {selectedSpecies.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedSpecies([])}
                className="text-xs text-primary font-semibold mt-2 hover:underline"
              >
                Clear selection
              </button>
            )}
          </div>

          {/* Location */}
          <div className="card">
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">Location</h3>
            <select
              className="form-input"
              value={selectedCounty}
              onChange={(e) => setSelectedCounty(e.target.value)}>

              <option value="All Counties">All Counties</option>
              {ukCounties.map((c) => <option key={`filter-county-${c}`} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Price */}
          <div className="card">
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">Max Price (£)</h3>
            <input
              type="number"
              className="form-input"
              placeholder="e.g. 5000"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)} />

          </div>

          {/* Verified only */}
          <div className="card">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-primary"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)} />

              <span className="text-sm font-semibold text-foreground">Verified sellers only</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1">{VERIFICATION_COPY.filterHint}</p>
          </div>

          {/* Sidebar ad */}
          <AdBanner width={300} height={250} label="300×250 Sidebar Rectangle" className="rounded-lg" />
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Sort bar */}
          <div className="flex items-center justify-between mb-4 gap-3">
            <span className="text-sm text-muted-foreground">
              Showing <span className="font-bold text-foreground">{listings.length}</span>
              {total > listings.length && <> of <span className="font-bold text-foreground">{total}</span></>} listings
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden sm:inline">Sort by:</span>
              <select
                className="form-input text-sm py-1.5 w-auto"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}>

                <option value="newest">Newest first</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="popular">Most viewed</option>
              </select>
            </div>
          </div>

          {/* Listings grid */}
          {error ?
          <div className="card flex flex-col items-center justify-center py-16 text-center" role="alert">
              <Icon name="ExclamationTriangleIcon" size={40} className="text-red-500 mb-3" />
              <h3 className="text-lg font-bold text-foreground mb-1">Couldn&apos;t load listings</h3>
              <p className="text-muted-foreground text-sm mb-4">{error}</p>
              <button type="button" onClick={load} className="btn-primary">Try again</button>
            </div> :
          loading ?
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="card p-0 overflow-hidden border border-border animate-pulse">
                  <div className="h-44 bg-muted" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-6 bg-muted rounded w-1/3 mt-4" />
                  </div>
                </div>
              ))}
            </div> :
          listings.length === 0 ?
          <div className="card flex flex-col items-center justify-center py-16 text-center">
              <Icon name="MagnifyingGlassIcon" size={40} className="text-muted-foreground mb-3" />
              <h3 className="text-lg font-bold text-foreground mb-1">No {categoryLabel} listings found</h3>
              <p className="text-muted-foreground text-sm mb-4">Try adjusting your filters or be the first to post in this category.</p>
              <Link href="/post-listing" className="btn-primary">Post a Listing</Link>
            </div> :

          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {listings.map((listing) =>
                <ListingCard key={listing.id} listing={listing} />
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="btn-secondary text-sm py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeftIcon" size={16} />
                  Previous
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="btn-secondary text-sm py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <Icon name="ChevronRightIcon" size={16} />
                </button>
              </div>
            )}
          </>
          }

          {/* Auction teaser */}
          <div className="mt-10 bg-primary rounded-2xl p-6 text-primary-foreground">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="BellAlertIcon" size={20} className="text-amber-300" />
                  <span className="text-amber-300 text-sm font-bold uppercase tracking-wider">Monthly Timed Auction</span>
                </div>
                <h3 className="text-xl font-extrabold text-white mb-1">
                  {activeCategory === 'farm-equipment' ? FARM_EQUIPMENT_LABEL : activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Auction
                </h3>
                <p className="text-green-200 text-sm">
                  Submit your lots now. Bidding opens at 9:00am. 48-hour timed auction format.
                </p>
              </div>
              <Link href="/timed-auctions" className="btn-accent shrink-0">
                <Icon name="BellAlertIcon" size={16} />
                View Auction Lots
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>);

}

/** Exported so the sector pages show listings identically to the marketplace. */
export function ListingCard({ listing }: { listing: Listing }) {
  const image = primaryImage(listing);
  const isLivestock = listing.category === 'livestock';
  const year = listing.details?.year_of_manufacture;
  const make = listing.details?.make_manufacturer;

  return (
    <div className="card p-0 overflow-hidden listing-card-hover border border-border">
      {/* Image */}
      <Link href={`/listing/${listing.id}`} className="block relative h-44 bg-muted overflow-hidden">
        {image ?
        <AppImage
          src={image}
          alt={listing.title}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 33vw" /> :
        <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
            <Icon name="PhotoIcon" size={28} />
            <span className="text-xs font-medium">No photo yet</span>
          </div>
        }

        {listing.details?.is_example === 'true' ? (
          <div className="absolute top-2 left-2">
            <span className="bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded">
              Example listing
            </span>
          </div>
        ) : listing.is_featured ? (
          <div className="absolute top-2 left-2">
            <span className="premium-badge inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-current" strokeWidth={1.75} aria-hidden="true" /> Featured</span>
          </div>
        ) : null}
        <div className="absolute top-2 right-2">
          {listing.is_verified_seller && <VerifiedBadge type="standard" />}
        </div>
        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded">
          {listedAgo(listing.created_at)}
        </div>
      </Link>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-foreground text-sm leading-tight mb-1 line-clamp-2">
          <Link href={`/listing/${listing.id}`} className="hover:text-primary transition-colors">
            {listing.title}
          </Link>
        </h3>

        {(listing.location_town || listing.county) && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            <Icon name="MapPinIcon" size={12} />
            <span>{[listing.location_town, listing.county].filter(Boolean).join(', ')}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-1 mb-2">
          {isLivestock && listing.breed && <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full font-medium">{listing.breed}</span>}
          {isLivestock && listing.age && <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full font-medium">{listing.age}</span>}
          {!isLivestock && year && <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full font-medium">{year}</span>}
          {!isLivestock && make && <span className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded-full font-medium">{make}</span>}
          {listing.quantity != null && <span className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded-full font-medium">Qty: {listing.quantity}</span>}
        </div>

        {listing.assurance_scheme && listing.assurance_scheme !== 'None' &&
        <div className="flex items-center gap-1 mb-2">
            <Icon name="ShieldCheckIcon" size={12} className="text-success" />
            <span className="text-xs text-success font-semibold">{listing.assurance_scheme}</span>
          </div>
        }

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
          <div>
            <div className="text-lg font-extrabold text-primary tabular-nums">
              {formatPrice(listing)}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <span className="flex items-center gap-0.5"><Icon name="EyeIcon" size={11} />{listing.view_count ?? 0}</span>
              <span className="flex items-center gap-0.5"><Icon name="ChatBubbleLeftIcon" size={11} />{listing.enquiry_count ?? 0} enquiries</span>
            </div>
          </div>
          <Link
            href={`/listing/${listing.id}`}
            className="btn-primary text-xs py-2 px-3">

            Enquire
          </Link>
        </div>
      </div>
    </div>);
}
